import React, { useState } from 'react'

import { useThemeProps, useTheme, Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import MuiPagination, { PaginationProps as MuiPaginationProps } from '@mui/material/Pagination'
import Select, { SelectProps } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import { TablePaginationProps } from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'

export type PaginationProps = {
  /**
   * The active color.
   * @default 'standard'
   */
  color?: MuiPaginationProps['color']
  /**
   * Items per page default value
   */
  defaultItemsPerPage?: number
  /**
   * If `true`, the select items per page component is disabled
   */
  disableChangeRowsPerPage?: boolean
  hideRowsPerPage?: boolean
  /**
   * The option of number items per page
   */
  itemsPerPageOptions?: number[]
  /**
   * Callback fired when the number of items per page is changed.
   */
  onItemsPerPageChange?: (itemsPerPage: number) => void
  /**
   * Callback fired when the page is changed.
   * @param {number} page The page selected.
   */
  onPageChange: (page: number, rowsPerPage: number) => void
  /**
   * The current page
   */
  page: number
  /**
   * The shape of the pagination items.
   * @default 'circular'
   */
  shape?: 'circular' | 'rounded'
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: MuiPaginationProps['size']
  /**
   * The total items
   */
  totalItems: number
  /**
   * The variant to use.
   * @default 'text'
   */
  variant?: MuiPaginationProps['variant']
}

export function Pagination(props: PaginationProps) {
  const {
    defaultItemsPerPage,
    disableChangeRowsPerPage = false,
    hideRowsPerPage = false,
    itemsPerPageOptions = [5, 10, 15],
    page = 1,
    onItemsPerPageChange,
    onPageChange,
    totalItems,
    ...rest
  } = props

  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })

  // workaround to get TablePaginationProps labelRowsPerPage translation
  const themeProps = useThemeProps<Theme, TablePaginationProps, 'MuiTablePagination'>({ props: {} as TablePaginationProps, name: 'MuiTablePagination' })

  const [itemsPerPage, setItemPerPage] = useState(defaultItemsPerPage || itemsPerPageOptions[0] || 10)

  const handleChangeItemsPerPage: SelectProps<string>['onChange'] = (event) => {
    const value = parseInt(event.target.value)
    if (itemsPerPage === value) return
    setItemPerPage(value)
    onItemsPerPageChange?.(value)
    // if (Math.ceil(totalItems / value) < page) onPageChange(Math.ceil(totalItems / value), value)
  }

  return (
    <Box
      component="div"
      sx={{
        display: 'inline-lex',
        justifyContent: 'start',
        alignItems: 'center',
      }}
    >
      {!hideRowsPerPage && !isMobileScreen && (
        <Stack alignItems="center" direction="row" spacing={2}>
          <Typography variant="body2">{themeProps.labelRowsPerPage ?? 'Rows per page:'}</Typography>
          <Select disabled={disableChangeRowsPerPage} value={`${itemsPerPage}`} onChange={handleChangeItemsPerPage} size="small">
            {itemsPerPageOptions.map((opt, idx) => (
              <MenuItem key={`${opt}-${idx}`} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}
      <MuiPagination
        {...rest}
        count={Math.ceil(totalItems / itemsPerPage)}
        page={page}
        onChange={(event: unknown, page: number) => onPageChange(page, itemsPerPage)}
        showFirstButton
        showLastButton
        siblingCount={isMobileScreen ? 0 : 1}
        size={isMobileScreen ? 'small' : 'medium'}
      />
    </Box>
  )
}

export default Pagination
